import { useMemo, type FC } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronDownIcon } from 'lucide-react';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import accessLevelApi from '@/app/common/api/accessLevelApi';
import { setDomain } from '@/app/common/reducers/domainSlice';
import DomainFavicon from '@/app/common/components/DomainFavicon';
import { cn } from '@/lib/utils';
import { domainMentionLabel } from '../utils/domainMentionMarkers';
import {
  filterDomains,
  McpDomainMenuBody,
  type McpUserDomain
} from './McpDomainMenu';
import {
  McpMenuShell,
  menuStyleAbove,
  useSearchMenuPopover
} from './McpSearchMenu';

type SelectedDomainState = {
  slug?: string | null;
};

export const McpDomainPicker: FC = () => {
  const dispatch = useDispatch();
  const selectedDomain = useSelector(
    (state: { selectedDomain?: SelectedDomainState }) =>
      state.selectedDomain ?? {}
  );
  const { data: userInfo, isLoading } = useGetUserInfoQuery();
  const domains = (userInfo?.data?.domains ?? []) as McpUserDomain[];

  const current = useMemo(() => {
    if (!domains.length) return null;
    const matched = domains.find(d => d.slug === selectedDomain?.slug);
    return matched ?? domains[0];
  }, [domains, selectedDomain?.slug]);

  const menu = useSearchMenuPopover({
    items: domains,
    filter: filterDomains,
    isSelected: d => d.slug === current?.slug,
    onPick: domain => {
      dispatch(
        setDomain({
          name: domain.url,
          slug: domain.slug,
          show_name: domain.show_name
        })
      );
      dispatch(accessLevelApi.util.invalidateTags(['AccessLevel']));
    }
  });

  const triggerLabel = current
    ? domainMentionLabel(current)
    : isLoading
      ? '...'
      : 'انتخاب دامنه';

  return (
    <>
      <button
        ref={menu.triggerRef}
        type="button"
        className={cn(
          'mcp-domain-trigger d-inline-flex align-items-center gap-2 border-0 rounded-2 bg-transparent text-600 px-2 py-1',
          menu.open && 'is-open'
        )}
        disabled={isLoading || domains.length === 0}
        aria-haspopup="listbox"
        aria-expanded={menu.open}
        aria-label={triggerLabel}
        onClick={menu.toggle}
      >
        {current?.url ? (
          <DomainFavicon
            domain={current.url}
            width={16}
            className="rounded-circle flex-shrink-0"
          />
        ) : null}
        <span className="text-truncate fw-medium fs--1" style={{ maxWidth: '16rem' }}>
          {triggerLabel}
        </span>
        <ChevronDownIcon
          className={cn('mcp-picker-chevron flex-shrink-0 opacity-55', menu.open && 'is-open')}
          size={14}
          strokeWidth={2}
        />
      </button>

      {menu.anchor
        ? createPortal(
            <McpMenuShell
              ref={menu.menuRef}
              ariaLabel="انتخاب دامنه"
              style={menuStyleAbove(menu.anchor)}
            >
              <McpDomainMenuBody
                domains={menu.filtered}
                totalCount={domains.length}
                isLoading={isLoading}
                search={menu.search}
                onSearchChange={menu.setSearch}
                searchRef={menu.searchRef}
                searchInputProps={{ onKeyDown: menu.onSearchKeyDown }}
                activeIndex={menu.activeIndex}
                onActiveIndexChange={menu.setActiveIndex}
                onSelect={menu.pick}
                selectedSlug={current?.slug}
              />
            </McpMenuShell>,
            document.body
          )
        : null}
    </>
  );
};
